import moment from "moment-timezone";
import { ReactNode, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SendEmailMutation } from "src/api/Email";
import { OfferingQuery } from "src/api/Offering";
import { PrivateSeriesForUserQuery } from "src/api/PrivateSerie";
import LinkButton from "src/components/buttons/LinkButton";
import PaymentTable from "src/components/layout/PaymentTable";
import InvoiceModel from "src/models/InvoiceModel";
import OfferingModel from "src/models/OfferingModel";
import PrivateSerieModel from "src/models/PrivateSerieModel";
import StudentInfoRequestResponseModel from "src/models/StudentInfoRequestResponseModel";
import UserModel from "src/models/UserModel";
import { apiClient, useApi } from "src/stores/Api";
import { useSession } from "src/stores/Session";
import { useStore } from "src/stores/Store";
import { dateFormat, days } from "src/utils/constants";
import { getInvoiceItemForPrivateSerie } from "src/utils/getInvoiceItemForPrivateSerie";
import getSimplifiedInvoiceItems from "src/utils/getSimplifiedInvoiceItems";
import message from "src/utils/message";
import { modals, useModal } from "src/utils/modals";
import tw from "src/utils/tw";
import {
  Level,
  PrivateSerieStatus,
  offering,
  sendEmail,
  sendEmailVariables,
} from "types/code-generator";
import { v4 as uuid } from "uuid";
import { AgeButton } from "../age/AgeButton";
import { AgesContainer } from "../age/AgesContainer";
import { BorderButton } from "../buttons/BorderButton";
import RoundedButton from "../buttons/RoundedButton";
import { ParticipantInfoRequest } from "../forms/ParticipantInfoRequests";
import InputCheckbox from "../inputs/InputCheckbox";
import InputDate from "../inputs/InputDate";
import InputError from "../inputs/InputError";
import InputRadio from "../inputs/InputRadio";
import { InputText } from "../inputs/InputText";
import InputTextarea from "../inputs/InputTextarea";
import Modal from "./Modal";

interface Props {
  offering: OfferingModel;
  privateSerie?: PrivateSerieModel;
  isCustom?: boolean;
  initialStep?: Step;
  disableNewParticipants?: boolean;
}

type Step =
  | "students"
  | "multiple"
  | "number"
  | "availability"
  | "existing"
  | "infoRequests"
  | "summary";

const steps: Step[] = [
  "students",
  "multiple",
  "number",
  "availability",
  "existing",
  "infoRequests",
  "summary",
];

interface OrganizationContactVars {
  accountOwnerName: string;
  organizationName: string;
  adminName: string;
  message: string;
  accountUrl: string;
  heading: string;
}

const OrganizationContact = (vars: OrganizationContactVars) => ({
  subject: `Message from ${vars.accountOwnerName} - ${vars.organizationName}`,
  html: `
<div>${vars.organizationName}</div>
<h1><strong>${vars.heading}</strong></h1>

<p>Dear ${vars.adminName},</p>

<p>${vars.message}</p>

<p><a href="${vars.accountUrl}">Manage ${vars.accountOwnerName}’s account</a></p>

<p>Sincerely,<br />Captyn</p>
`,
});

export const AddStudentToPrivateSerieModal = modals.create((props: Props) => {
  const newPrivateSerie =
    props.privateSerie ||
    new PrivateSerieModel({
      offering: props.offering,
      status: PrivateSerieStatus.Pending,
    });

  if (window.sessionStorage.getItem("createPrivateSerie")) {
    const existingCreateOffering = JSON.parse(
      window.sessionStorage.getItem("createOffering")
    );
    const existingPrivateSerie = JSON.parse(
      window.sessionStorage.getItem("createPrivateSerie")
    );

    if (existingCreateOffering) {
      newPrivateSerie.offering.price = existingCreateOffering.price || 0;
      newPrivateSerie.offering.location =
        existingCreateOffering.location || null;
    }

    if (existingPrivateSerie) {
      newPrivateSerie.numberOfLessons =
        existingPrivateSerie.numberOfLessons || 0;
    }
  }

  const session = useSession();
  const location = useLocation();
  const [areTogether, setAreTogether] = useState(true);
  const [visited, setVisited] = useState(
    props.initialStep ? [props.initialStep] : ["students"]
  );
  const [students, setStudents] = useState(session.students || []);
  const [privateSerie, setPrivateSerie] = useState(newPrivateSerie);
  const [numberOfLessons, setNumberOfLessons] = useState(
    props.privateSerie
      ? props.privateSerie.numberOfLessons
      : props.offering.minAttendances
        ? props.offering.minAttendances
        : 0
  );
  const [customNumber, setCustomNumber] = useState(
    Boolean(privateSerie.numberOfLessons > 9)
  );
  const [errors, setErrors] = useState<any>({});
  const [currentStudent, setCurrentStudent] = useState(null);
  const modal = useModal();
  const store = useStore();
  const history = useHistory();

  const privateSeriesQuery = useApi(PrivateSeriesForUserQuery, {
    skip: !store.user || !modal.visible || store.user?.isAdminOrAbove,
    variables: {
      where: {
        accountOwnerId: store.user?.id,
        status: {
          in: [PrivateSerieStatus.InProgress, PrivateSerieStatus.Pending],
        },
      },
    },
  });

  const serieInOffering = privateSeriesQuery.privateSeries?.find(
    (p) =>
      !store.user?.isAdminOrAbove &&
      p.offering?.id === privateSerie.offering?.id &&
      p.offering?.department?.id === privateSerie.offering?.department?.id &&
      privateSerie.roster.length > 0 &&
      privateSerie.roster.length === p.roster.length &&
      privateSerie.roster.every((s) => p.roster.find((rs) => rs.id === s.id))
  );
  const serieInOtherOffering = privateSeriesQuery.privateSeries?.find(
    (p) =>
      !store.user?.isAdminOrAbove &&
      p.offering?.id !== privateSerie.offering?.id &&
      p.offering?.department?.id === privateSerie.offering?.department?.id &&
      p.offering?.allowExtension &&
      privateSerie.roster.length > 0 &&
      privateSerie.roster.length === p.roster.length &&
      privateSerie.roster.every((s) => p.roster.find((rs) => rs.id === s.id))
  );

  // console.log("serieInOffering", serieInOffering);
  // console.log("serieInOtherOffering", serieInOtherOffering);
  // console.log("privateSerie", privateSerie);

  const currentStep = visited[visited.length - 1];
  const multipleRegistrations =
    students.length > 1 && privateSerie.roster.length === 1 && currentStudent;

  let currentIndex = steps.findIndex((s) => s === currentStep);

  if (currentIndex < 0) {
    currentIndex = 0;
  }

  useEffect(() => {
    if (students.length === 0) {
      addNewStudent();
    }
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length) {
      setErrors({});
    }
  }, [privateSerie, currentStep]);

  function handleCancel() {
    modal.hide();
  }

  async function validateCurrentStep() {
    const errors = {};

    if (currentStep === "students") {
      if (
        privateSerie.offering.minParticipants &&
        privateSerie.offering.maxParticipants
      ) {
        const offeringMin = privateSerie.offering.minParticipants;
        const offeringMax = privateSerie.offering.maxParticipants;
        const valid =
          privateSerie.roster.length >= offeringMin &&
          privateSerie.roster.length <= offeringMax;

        if (!valid) {
          errors["roster"] =
            `This ${privateSerie.offering.type.toLowerCase()} must include at least ${offeringMin} and no more than ${offeringMax} ${privateSerie.offering.participantType.toLowerCase()}s per registration.`;
        }
      }

      if (privateSerie.roster.length === 0) {
        errors["roster"] = "Please select at least one participant";
      }

      // setStudents((current) =>
      //   current.filter((s) => privateSerie.roster.find((rs) => rs.id === s.id))
      // );

      return errors;
    }

    if (currentStep === "number" && !numberOfLessons) {
      errors["numberOfLessons"] = "Please select an amount";
    }

    if (currentStep === "availability") {
      if (
        props.offering.availableDays.length > 0 &&
        privateSerie.availableDays.length === 0
      ) {
        errors["availableDays"] = "Please select at least one day";
      }

      if (props.offering.requestStartDate) {
        if (!privateSerie.firstLessonStart) {
          errors["firstLessonStart"] = "Please enter a date";
        }

        if (!privateSerie.firstLessonEnd) {
          errors["firstLessonEnd"] = "Please enter a date";
        }

        if (privateSerie.firstLessonStart && privateSerie.firstLessonEnd) {
          const firstLessonStart = moment(privateSerie.firstLessonStart);
          const firstLessonEnd = moment(privateSerie.firstLessonEnd);

          if (firstLessonStart.isAfter(firstLessonEnd)) {
            errors["firstLessonStart"] = "Invalid dates";
          }
        }
      }
    }

    if (currentStep === "multiple") {
      if (!areTogether) {
        const participant = privateSerie.roster[0];
        updatePrivateSerie({
          roster: [participant],
        });

        setCurrentStudent(participant);
      }
    }

    if (currentStep === "infoRequests") {
      for (const sir of privateSerie.offering.studentInfoRequests) {
        const hasAnswer = privateSerie.studentInfoRequestResponses.find(
          (sirr) => sirr.studentInfoRequest.id === sir.id && sirr.description
        );

        if (sir.required && !hasAnswer) {
          errors[sir.id] = "Please enter a response";
        }
      }
    }

    return errors;
  }

  async function sendAdminEmail({ close = true }) {
    const accountOwnerName = `${store.user?.firstName} ${store.user?.lastName}`;

    const email = OrganizationContact({
      accountOwnerName,
      organizationName: store.organization.title,
      adminName: store.organization.contactName,
      message: `${accountOwnerName} has sent a message during registration for a Custom ${serieInOffering?.offering?.type} template:<br /><br />“${privateSerie.note}”`,
      accountUrl: `https://${store.organization.subdomain}.captyn.com/admin/users/${store.user?.id}`,
      heading: "Registration Message",
    });

    const to = [];

    if (privateSerie.instructor?.email) {
      to.push(privateSerie.instructor.id);
    }

    if (
      props.privateSerie?.instructor?.email &&
      !to.includes(props.privateSerie.instructor.email)
    ) {
      to.push(props.privateSerie.instructor.id);
    }

    const variables: sendEmailVariables = {
      data: {
        users: to,
        sendToOrganization: true,
        subject: email.subject,
        organizationId: store.organization.id,
        html: email.html,
      },
    };

    await apiClient<sendEmail, sendEmailVariables>(SendEmailMutation, {
      variables,
    });

    session.removeRegistration(privateSerie.id);

    if (props.privateSerie) {
      session.removeRegistration(props.privateSerie.id);
    }

    message.success("Message sent");

    if (close) {
      handleCancel();
    }
  }

  async function onNext(e?, forceNext: Step = null) {
    const errors = await validateCurrentStep();

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    if (props.isCustom) {
      const isSingleStudent =
        privateSerie.roster.length === 1 && currentStep === "students";
      const isMultipleStudents =
        (privateSerie.roster.length > 1 ||
          (privateSerie.roster.length === 1 && currentStudent)) &&
        currentStep === "multiple";

      if (isSingleStudent || isMultipleStudents) {
        window.sessionStorage.setItem(
          "createPrivateSerie",
          JSON.stringify(privateSerie)
        );

        if (privateSerie.offering) {
          window.sessionStorage.setItem(
            "createOffering",
            JSON.stringify(privateSerie.offering)
          );
        }
        session.setStudents(students);
        session.setReturnPathname(`${location.pathname}${location.search}`);
        history.push("/admin/private-lessons/add");
        handleCancel();
        return;
      }
    }

    let next = steps[currentIndex + 1];
    const offeringMax = privateSerie.offering.maxParticipants;

    console.log("start next", next);

    if (
      next === "multiple" &&
      (privateSerie.roster.length === 1 || props.privateSerie)
    ) {
      if (offeringMax > 1 && privateSerie.roster.length === 1) {
        next = "multiple";
      } else {
        next = "number";
      }
    }

    if (next === "number") {
      if (props.offering.minAttendances && props.offering.package) {
        updatePrivateSerie({ numberOfLessons: props.offering.minAttendances });
        next = "availability";
      }
    }

    if (next === "availability") {
      if (serieInOffering || serieInOtherOffering?.offering.allowExtension) {
        next = "existing";
      } else if (
        privateSerie.offering.studentInfoRequests.length > 0 &&
        privateSerie.offering.availableDays.length === 0
      ) {
        next = "infoRequests";
      } else if (
        !props.offering.requestStartDate &&
        privateSerie.offering.availableDays.length === 0
      ) {
        next = "summary";
      }
    }

    if (next === "existing") {
      if (!serieInOffering && !serieInOtherOffering) {
        next = "infoRequests";
      }
    }

    if (next === "infoRequests") {
      if (
        privateSerie.offering.studentInfoRequests.length === 0 ||
        (privateSerie.isExtending && serieInOffering)
      ) {
        next = "summary";
      }

      // console.log("serieInOtherOffering", serieInOtherOffering);
      // console.log("privateSerie", privateSerie);

      if (serieInOtherOffering && privateSerie.isExtending) {
        next = "existing";
      }
    }

    if (forceNext) {
      next = forceNext;
    }

    console.log("next", next);

    setVisited((current) => {
      return [...current, next];
    });
  }

  function onPrevious() {
    setVisited((current) => {
      current.pop();
      return [...current];
    });
  }

  function updatePrivateSerie(values) {
    setPrivateSerie(
      (current) =>
        new PrivateSerieModel({
          ...current,
          ...values,
        })
    );
  }

  function addNewStudent() {
    const newStudent = new UserModel({
      id: uuid(),
      firstName: `${privateSerie.offering.participantType} ${
        students.length + 1
      }`,
      level: Level.Student,
    });

    const newPrivateSerie = new PrivateSerieModel({
      ...privateSerie,
    });

    newPrivateSerie.roster.push(newStudent);

    const newStudents = [...students, newStudent];

    setPrivateSerie(newPrivateSerie);
    setStudents(newStudents);
  }

  function toggleStudentInSeries(checked, student) {
    // console.log("checked", checked, student);

    const newPrivateSerie = new PrivateSerieModel({
      ...privateSerie,
    });

    if (checked) {
      newPrivateSerie.roster.push(student);
    } else {
      const newRoster = newPrivateSerie.roster.filter(
        (s) => s.id !== student.id
      );
      newPrivateSerie.roster = newRoster;
    }

    setPrivateSerie(newPrivateSerie);
  }

  function setStudentName(firstName, student) {
    const newStudents = [...students];
    const newStudentIndex = newStudents.findIndex((s) => s.id === student.id);

    if (newStudentIndex >= 0) {
      newStudents[newStudentIndex].firstName = firstName;
      setStudents(newStudents);
    }
  }

  function renderStudents() {
    const checkboxes = [];

    for (const s of students) {
      const isInSeries = privateSerie.roster.find((rs) => rs.id === s.id);
      const initialValue = s.firstName;
      const isExisting = !s.id.includes("-");

      checkboxes.push(
        <div key={s.id}>
          <div className={tw("flex", isExisting && "items-center")}>
            <InputCheckbox
              checked={Boolean(isInSeries)}
              onChange={(checked) => toggleStudentInSeries(checked, s)}
            />
            <div className="flex-1">
              <div className="step-subhead relative z-10 -mb-1 pl-4 text-2xs">
                First Name
              </div>
              {!isExisting && (
                <InputText
                  className="border-none bg-white"
                  autoFocus
                  placeholder={`Add New ${privateSerie.offering?.participantType}`}
                  value={initialValue}
                  onChange={(checked) => setStudentName(checked, s)}
                  selectTextOnFocus={Boolean(s.firstName)}
                />
              )}
              {isExisting && <div className="px-4">{s.firstName}</div>}
            </div>
          </div>
          <hr />
        </div>
      );
    }

    const body = (
      <div className="mb-6">
        <p>
          Select or add {privateSerie.offering?.participantType.toLowerCase()}s
          to this {privateSerie.offering.type.toLowerCase()}.
        </p>
        <hr />
        <div>{checkboxes}</div>
        <div className="mb-2">
          <InputError error={errors.roster} />
        </div>

        {!props.disableNewParticipants && (
          <BorderButton dashed className="w-full" onClick={addNewStudent}>
            <i
              aria-hidden
              className="far fa-plus"
              css={{ color: store.theme.orgColor }}
            />{" "}
            Add another {props.offering.participantType.toLowerCase()}
          </BorderButton>
        )}
      </div>
    );

    return { body, footer: defaultFooter };
  }

  function renderExisting() {
    let existing = new PrivateSerieModel(
      serieInOffering || serieInOtherOffering
    );

    if (props.privateSerie && props.initialStep === "existing") {
      existing = props.privateSerie;
    }

    const roster = existing.roster.map((r) => `${r.firstName} ${r.lastName}`);

    let message = (
      <>
        You are registering {numberOfLessons} additional session
        {numberOfLessons !== 1 && "s"}.
      </>
    );

    let label = (
      <>
        If you have any notes about this extension for{" "}
        {store.organization.title}, add them here.
      </>
    );

    let meta = (
      <>
        For a faster experience, you can extend your registration in your
        account.
      </>
    );

    let input = (
      <InputTextarea
        placeholder="Enter note (optional)"
        value={privateSerie.note}
        onChange={(note) => updatePrivateSerie({ note })}
      />
    );

    let footer = (
      <>
        <BorderButton
          type="submit"
          onClick={() => {
            const newPrivateSerie = {
              ...serieInOffering,
              isExtending: true,
            };

            delete newPrivateSerie.note;

            updatePrivateSerie(newPrivateSerie);

            if (serieInOffering.offering.extensionMinAttendances) {
              if (
                serieInOffering.offering.package ||
                numberOfLessons <
                  serieInOffering.offering.extensionMinAttendances
              ) {
                setNumberOfLessons(
                  serieInOffering.offering.extensionMinAttendances
                );
              }
            }

            if (privateSerie.note) {
              sendAdminEmail({ close: false });
            }

            onNext();
          }}
          disabled={Object.keys(errors).length > 0}
        >
          Next
        </BorderButton>
        <BorderButton onClick={handleCancel}>Cancel</BorderButton>
      </>
    );

    if (!existing?.offering?.allowExtension) {
      message = <>Registration extension is not available</>;
      label = (
        <>
          Leave a note or make a request about your existing registration here.
          We’ll deliver your note to {store.organization.title}.
        </>
      );
      meta = null;
      footer = (
        <>
          <BorderButton
            type="submit"
            className="w-full"
            onClick={sendAdminEmail}
          >
            Send
          </BorderButton>
        </>
      );
    }

    if (serieInOtherOffering && serieInOtherOffering.offering?.allowExtension) {
      message = (
        <>
          <div>
            A prior registration, first registered on{" "}
            {moment(existing.createdAt).format(dateFormat)}, has{" "}
            {existing.getRemainingLessons()} sessions remaining.
          </div>
          {existing.instructor && (
            <div className="text-sm text-empty">
              Assigned instructor • {existing.instructor.firstName}{" "}
              {existing.instructor.lastName}
            </div>
          )}
        </>
      );

      label = (
        <>
          Would you like to extend this registration by {numberOfLessons}{" "}
          sessions?
        </>
      );

      input = (
        <div className="flex flex-col gap-4">
          <BorderButton
            type="submit"
            className="w-full"
            onClick={() => {
              updatePrivateSerie({
                ...serieInOtherOffering,
                isExtending: true,
              });

              onNext(null, "existing");
            }}
          >
            Yes, extend the prior registration
          </BorderButton>
          <BorderButton
            type="submit"
            className="w-full"
            onClick={() => {
              updatePrivateSerie({
                isNew: true,
              });

              if (props.initialStep) {
                handleCancel();
              } else {
                onNext();
              }
            }}
          >
            No, create a new registration
          </BorderButton>
          <BorderButton className="w-full" onClick={handleCancel}>
            Cancel
          </BorderButton>
        </div>
      );

      meta = null;
      footer = null;
    }

    const body = (
      <>
        <div className="mb-4 flex">
          <div className="mr-2">
            <i
              className="far fa-triangle-exclamation"
              css={{ color: store.theme.orgColor }}
            />
          </div>
          <div className="flex-1 font-bold">
            {roster.join(", ")} {roster.length > 1 ? "are" : "is"} already
            registered for{" "}
            {privateSerie.offering.title ??
              privateSerie.offering.program?.title}
          </div>
        </div>
        <div className="mb-2">{message}</div>
        <div className="mb-4">
          <strong className="mb-2 block">{label}</strong>
          {input}
          {meta && <div className="pt-2 text-sm text-empty">{meta}</div>}
        </div>
      </>
    );

    return { body, footer };
  }

  function renderMultiple() {
    let body = (
      <>
        <p>
          Are {privateSerie.roster.map((rs) => rs.firstName).join(", ")}{" "}
          participating together?
        </p>

        <div className="mb-2">
          <InputRadio
            value={true}
            checked={areTogether}
            onChange={setAreTogether}
            label="Yes, they will be together for each session."
          />
        </div>

        <div className="mb-8">
          <InputRadio
            value={false}
            checked={areTogether === false}
            onChange={setAreTogether}
            label="No, they will be independent registrations."
          />
        </div>
      </>
    );

    if (
      privateSerie.offering.maxParticipants > 1 &&
      privateSerie.roster.length === 1
    ) {
      body = (
        <>
          <p>
            You’re adding a <strong>one-on-one</strong> registration.
          </p>

          <p>
            <LinkButton onClick={onPrevious}>Go back</LinkButton> if you want to
            add another {privateSerie.offering.participantType.toLowerCase()} to
            this registration.
          </p>

          <p>Otherwise, ‘Next’</p>
        </>
      );
    }

    return { body, footer: defaultFooter };
  }

  function renderNumber() {
    const body = (
      <>
        {multipleRegistrations && (
          <div className="mb-2 font-bold">
            <i
              aria-hidden
              className="far fa-child"
              css={{ color: store.theme.orgColor }}
            />{" "}
            {currentStudent?.firstName}’s registration
          </div>
        )}
        <p className="mb-4">
          Choose the number of sessions for this initial registration.
        </p>

        {privateSerie.offering.minAttendances && (
          <p>
            <strong>
              {privateSerie.offering.minAttendances} session minimum
            </strong>
          </p>
        )}
        <div className="flex w-80 flex-wrap items-center gap-x-4 gap-y-2">
          {!customNumber &&
            Array(9)
              .fill(0)
              .filter((_, i) => {
                if (privateSerie.offering.maxInitial) {
                  return i < privateSerie.offering.maxInitial;
                }

                return true;
              })
              .map((k, i) => {
                let amount = i + 1;

                if (privateSerie.offering.minAttendances) {
                  amount = i + privateSerie.offering.minAttendances;
                }

                return (
                  <div className="w-12" key={amount}>
                    <RoundedButton
                      onClick={() => {
                        setNumberOfLessons(amount);
                      }}
                      active={numberOfLessons === amount}
                    >
                      <span className="font-normal">{amount}</span>
                    </RoundedButton>
                  </div>
                );
              })}

          {customNumber && (
            <div className="flex-1">
              <InputText
                value={numberOfLessons || ""}
                name="initialNumberOfLessons"
                placeholder="Enter number"
                onChange={(numberOfLessons) => {
                  if (privateSerie.offering.maxInitial) {
                    const maxInitial = privateSerie.offering.maxInitial;

                    if (numberOfLessons > maxInitial) {
                      return;
                    }
                  }

                  setNumberOfLessons(numberOfLessons);
                }}
                type="tel"
                maxLength={3}
              />
            </div>
          )}

          <div className="w-12">
            <RoundedButton
              onClick={() => setCustomNumber(!customNumber)}
              active={customNumber}
            >
              <span className="font-normal">+</span>
            </RoundedButton>
          </div>
        </div>

        <InputError error={errors.numberOfLessons} />
      </>
    );

    return { body, footer: defaultFooter };
  }

  function renderAvailability() {
    let minDate = moment();

    if (props.offering.firstLessonStart) {
      minDate = moment(props.offering.firstLessonStart);
    } else if (props.offering.firstLessonStartDays) {
      minDate = minDate.add(props.offering.firstLessonStartDays, "days");
    }

    const body = (
      <>
        {multipleRegistrations && (
          <div className="mb-2 font-bold">
            <i
              aria-hidden
              className="far fa-child"
              css={{ color: store.theme.orgColor }}
            />{" "}
            {currentStudent?.firstName}’s registration
          </div>
        )}

        {props.offering.availableDays.length > 0 && (
          <div className="mb-4">
            <p className="mb-2">
              What days of the week are you available for scheduling?
            </p>
            <AgesContainer>
              {days
                .filter((d) => props.offering.availableDays.includes(d.value))
                .map((d) => {
                  const isSelected = privateSerie.availableDays.includes(
                    d.value
                  );

                  return (
                    <AgeButton
                      isSelected={isSelected}
                      age={d.label}
                      key={d.label}
                      onClick={() => {
                        updatePrivateSerie({
                          availableDays: isSelected
                            ? privateSerie.availableDays.filter(
                                (ad) => ad !== d.value
                              )
                            : [...privateSerie.availableDays, d.value],
                        });
                      }}
                    >
                      {d.label}
                    </AgeButton>
                  );
                })}
            </AgesContainer>
            <InputError error={errors.availableDays} />
          </div>
        )}

        {props.offering.requestStartDate && (
          <>
            <div className="mb-4">
              <p className="mb-2">
                What is the date range for when you’d like to start?
              </p>
              <InputDate
                value={privateSerie.firstLessonStart}
                endValue={privateSerie.firstLessonEnd}
                onChange={(firstLessonStart, firstLessonEnd) =>
                  updatePrivateSerie({ firstLessonStart, firstLessonEnd })
                }
                placeholder="Date Range"
                minDate={minDate.toDate()}
                range
                allowClear
              />
              <InputError
                error={errors.firstLessonStart || errors.firstLessonEnd}
              />
            </div>
          </>
        )}
      </>
    );

    return { body, footer: defaultFooter };
  }

  function updateInfoRequestResponse(
    infoRequestResponse: StudentInfoRequestResponseModel
  ) {
    // console.log("infoRequestResponse", infoRequestResponse);

    const newInfoRequestResponses =
      privateSerie.studentInfoRequestResponses.filter(
        (i) =>
          i.studentInfoRequest.id !== infoRequestResponse.studentInfoRequest.id
      );

    newInfoRequestResponses.push(infoRequestResponse);

    updatePrivateSerie({
      studentInfoRequestResponses: newInfoRequestResponses,
    });
  }

  function renderInfoRequests() {
    const infoRequests = privateSerie.offering.studentInfoRequests
      .sort((a, b) => a.order - b.order)
      .map((ir) => {
        const existingInfoRequestResponse =
          privateSerie.studentInfoRequestResponses.find(
            (i) => i.studentInfoRequest.id === ir.id
          );
        const error = errors[ir.id];

        const hasAdditional =
          (existingInfoRequestResponse?.description === "Yes" &&
            ir.yesQuestion) ||
          (existingInfoRequestResponse?.description === "No" && ir.noQuestion);

        return (
          <ParticipantInfoRequest
            key={ir.id}
            infoRequest={ir as any}
            existingInfoRequestResponse={existingInfoRequestResponse}
            hasAdditional={Boolean(hasAdditional)}
            updateInfoRequestResponse={updateInfoRequestResponse}
            error={error}
          />
        );
      });

    const body = (
      <>
        {multipleRegistrations && (
          <div className="mb-2 font-bold">
            <i
              aria-hidden
              className="far fa-child"
              css={{ color: store.theme.orgColor }}
            />{" "}
            {currentStudent?.firstName}’s registration
          </div>
        )}
        {infoRequests}
      </>
    );

    return { body, footer: defaultFooter };
  }

  function renderSummary() {
    const names = privateSerie.roster.map((rs) => rs.firstName);
    const summary = `${numberOfLessons} session${
      numberOfLessons !== 1 ? "s" : ""
    } for ${names.join(", ")}`;
    const studentIndex = students.findIndex((s) => s.id === currentStudent?.id);
    const isLastStudent = studentIndex === students.length - 1;
    const nextStudent = students[studentIndex + 1];
    let footer = (
      <>
        <BorderButton type="submit" onClick={addToCart}>
          Add to Cart
        </BorderButton>
        <BorderButton onClick={handleCancel}>Cancel</BorderButton>
      </>
    );

    const newPrivateSerie = new PrivateSerieModel({
      ...privateSerie,
      numberOfLessons,
    });

    const invoiceItem = getInvoiceItemForPrivateSerie({
      registration: newPrivateSerie,
      startDate: new Date(),
      coupons: [],
      organization: store.organization,
    });

    const simplifiedInvoiceItems = getSimplifiedInvoiceItems([invoiceItem]);

    const invoice = new InvoiceModel({
      invoiceItems: simplifiedInvoiceItems,
    });

    // console.log("newPrivateSerie", newPrivateSerie);

    let note = privateSerie.availableDays.length > 0 &&
      privateSerie.firstLessonStart && (
        <div>
          Available {privateSerie.availableDays.join(", ")} starting{" "}
          {moment(privateSerie.firstLessonStart).format(dateFormat)}
        </div>
      );

    if (serieInOffering) {
      note = <div>{privateSerie.note}</div>;
    }

    // console.log("studentIndex", studentIndex);

    if (currentStudent && !isLastStudent) {
      footer = (
        <>
          <BorderButton
            type="submit"
            onClick={() => {
              addToCart({ continueAdding: true });
              setCurrentStudent(nextStudent);
              setVisited(["number"]);
              setPrivateSerie(
                new PrivateSerieModel({
                  offering: props.offering,
                  roster: [nextStudent],
                })
              );
              setNumberOfLessons(props.offering.minAttendances || 0);
            }}
          >
            Next
          </BorderButton>
          <BorderButton onClick={handleCancel}>Cancel</BorderButton>
        </>
      );
    }

    const body = (
      <>
        <h2 className="mb-2">{privateSerie.offering.type} Summary</h2>
        <div>
          <strong>{summary}</strong>
        </div>
        {note}
        <hr />
        <PaymentTable fullWidth invoice={invoice} />
      </>
    );

    return { body, footer };
  }

  async function addToCart({ continueAdding } = { continueAdding: false }) {
    const processingMessage = message.loading("Adding…");

    let offeringData = privateSerie.offering;

    if (privateSerie.offering.id) {
      const fetchOfferingData = await apiClient<offering>(OfferingQuery, {
        variables: {
          id: privateSerie.offering.id,
        },
      });

      offeringData = new OfferingModel(fetchOfferingData.offering);
    }

    if (privateSerie.roster.length === 0) {
      if (privateSerie.id) {
        session.removeRegistration(privateSerie.id);
      }

      handleCancel();
      return;
    }

    const newPrivateSerie = new PrivateSerieModel({
      ...privateSerie,
      offering: offeringData,
      numberOfLessons,
    });

    if (currentStudent) {
      newPrivateSerie.roster = [currentStudent];
    }

    session.addPrivateSerie(newPrivateSerie);

    if (props.initialStep) {
      // Private serie was passed from checkout, so remove existing
      session.removeRegistration(props.privateSerie.id);
    }

    const studentsWithNames = students.filter(
      (s) =>
        s.firstName !== "" &&
        newPrivateSerie.roster.find((rs) => rs.id === s.id)
    );

    session.setStudents((current) => {
      const newStudents = [...current];

      for (const s of studentsWithNames) {
        if (!newStudents.find((ns) => ns.id === s.id)) {
          newStudents.push(s);
        }
      }

      return newStudents;
    });

    processingMessage();

    if (!continueAdding) {
      handleCancel();
    }
  }

  let content: { body: ReactNode; footer: ReactNode };

  const defaultFooter = (
    <>
      <BorderButton
        type="submit"
        onClick={onNext}
        disabled={Object.keys(errors).length > 0}
      >
        Next
      </BorderButton>
      <BorderButton onClick={handleCancel}>Cancel</BorderButton>
    </>
  );

  console.log("currentStep", currentStep);
  console.log("privateSerie", privateSerie);

  if (currentStep === "students") {
    content = renderStudents();
  } else if (currentStep === "multiple") {
    content = renderMultiple();
  } else if (currentStep === "number") {
    content = renderNumber();
  } else if (currentStep === "availability") {
    content = renderAvailability();
  } else if (currentStep === "infoRequests") {
    content = renderInfoRequests();
  } else if (currentStep === "summary") {
    content = renderSummary();
  } else if (currentStep === "existing") {
    content = renderExisting();
  }

  // console.log("privateSerie", privateSerie);
  // console.log("currentStep", currentStep);
  // console.log("currentStudent", currentStudent);

  return (
    <Modal
      visible={modal.visible}
      afterClose={modal.remove}
      handleCancel={handleCancel}
      footer={content.footer}
      title={`${props.offering.type} Registration`}
    >
      {visited.length > 1 && (
        <div className="mb-4">
          <button className="text-sm text-empty" onClick={onPrevious}>
            <i aria-hidden className="far fa-circle-arrow-left" /> Previous
          </button>
        </div>
      )}
      {content.body}
    </Modal>
  );
});
